import type React from 'react';
import { parseMessageContent } from '../../utils/messageParser';
import './MessageContent.css';

interface MessageContentProps {
  content: string;
  onQuestionClick?: (question: string) => void;
  isAssistant: boolean;
  /** Optional inline element to show at the end of the main text (like token usage icon) */
  inlineSuffix?: React.ReactNode;
  /** Explicit companion name to display at the top of assistant replies */
  companionName?: string;
  /** When true, follow-up buttons are disabled (e.g. while a message is loading) */
  disabled?: boolean;
  /** When false, the suggested follow-up questions section is hidden. Defaults to true. */
  showFollowUp?: boolean;
}

export const MessageContent: React.FC<MessageContentProps> = ({
  content,
  onQuestionClick,
  isAssistant,
  inlineSuffix,
  companionName,
  disabled,
  showFollowUp = true,
}) => {
  if (!isAssistant) {
    return (
      <p className="message-text">
        {content}
        {inlineSuffix}
      </p>
    );
  }

  const { mainContent, headingLine, followUpQuestions } = parseMessageContent(content);

  return (
    <div className="message-content-root">
      {isAssistant && companionName && (
        <div className="message-companion-name">{companionName}</div>
      )}

      {mainContent && (
        <p className="message-text">
          {mainContent}
          {inlineSuffix}
        </p>
      )}

      {!mainContent && inlineSuffix && <div className="inline-suffix-only">{inlineSuffix}</div>}

      {showFollowUp && followUpQuestions.length > 0 && (
        <div className="follow-up-section">
          {headingLine && <div className="follow-up-heading">{headingLine}</div>}
          <div className="questions-stack">
            {followUpQuestions.map((q) => (
              <button
                key={q.display}
                type="button"
                className="follow-up-btn"
                onClick={() => onQuestionClick?.(q.action)}
                title={q.display}
                disabled={disabled}
              >
                <div className="follow-up-icon">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <span className="follow-up-text">{q.display}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageContent;
